<template>
  <div class="p-5 pt-1">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="grid grid-cols-5 gap-8 mt-6">
      <div class="col-span-3">
        <div class="bg-white rounded-2xl py-8 px-4 h-full">
          <div class="flex flex-wrap flex-row items-center justify-start">
            <div class="flex flex-col w-1/2 px-4">
              <label class="inline-block text-sm font-semibold text-grey p-0 mb-2">
                Barge Name
              </label>
              <vs-input
                class="min-w-full"
                placeholder="Enter barge name"
                v-model="form.name.value"
                :class="{ 'is-not-valid': !form.name.isValid }"
                :disabled="isDetail()"
              />
            </div>
            <div class="flex flex-col w-1/2 px-4">
              <label class="inline-block text-sm font-semibold text-grey p-0 mb-2">
                Owner Name
              </label>
              <select-with-search
                class="min-w-full"
                placeholder="Select owner name"
                v-model="form.owner_id.value"
                :class="{ 'is-not-valid': !form.owner_id.isValid }"
                :disabled="isDetail()"
                :options="ownerOptions"
              />
            </div>
            <div class="flex flex-col w-1/2 px-4 pt-5">
              <label class="inline-block text-sm font-semibold text-grey p-0 mb-2">
                Barge Size
              </label>
              <autonumeric
                class="min-w-full"
                placeholder="Enter barge size"
                :options="numericOptions"
                v-model="form.barge_size.value"
                :class="{ 'is-not-valid': !form.barge_size.isValid }"
                :disabled="isDetail()"
              >
                <div class="h-full px-5 flex flex-col justify-center rounded-r-lg bg-primary w-16">
                  <span class="text-white text-center">ft</span>
                </div>
              </autonumeric>
            </div>
            <div class="flex flex-col w-1/2 px-4 pt-5">
              <label class="inline-block text-sm font-semibold text-grey p-0 mb-2">
                Barge Capacity
              </label>
              <autonumeric
                class="min-w-full"
                placeholder="Enter barge capacity"
                :options="numericOptions"
                v-model="form.barge_capacity.value"
                :class="{ 'is-not-valid': !form.barge_capacity.isValid }"
                :disabled="isDetail()"
              >
                <div class="h-full px-5 flex flex-col justify-center rounded-r-lg bg-primary w-16">
                  <span class="text-white text-center">mt</span>
                </div>
              </autonumeric>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-2">
        <div class="bg-white rounded-2xl p-6">
          <div class="image-wrapper">
            <input
              ref="inputImage"
              class="hidden"
              type="file"
              accept="image/*"
              @input="imageToBase64"
            >
            <img
              :class="{ 'image': !!image }"
              :src="image || require('@/assets/shipo-default-image.png')"
            >
            <div
              v-if="!isDetail()"
              :class="[!!image ? 'bg-icon delete' : 'bg-icon']"
              @click="!!image ? clearImage() : $refs.inputImage.click()"
            >
              <img :src="require(`@/assets/shipo-icon-${!!image ? 'delete' : 'edit'}.svg`)">
            </div>
          </div>
        </div>
        <div
          v-if="!isCreate()"
          class="flex flex-wrap flex-row items-center justify-between bg-white rounded-2xl p-6 mt-8"
        >
          <span class="font-semibold">Is Active</span>
          <vs-switch
            v-model="isActive"
            color="secondary"
            :disabled="!isUserCanAccessMenu(permission.name, 'is_active')"
          />
        </div>
      </div>
    </div>
    <div class="mt-6 px-3 flex items-center justify-end">
      <vs-button
        class="button"
        type="border"
        color="secondary"
        @click="$router.push({ name: 'SettingsBargeManagementList' })"
      >
        {{ isDetail() ? 'Back' : 'Cancel' }}
      </vs-button>
      <vs-button
        v-if="!isDetail()"
        class="button shadow-secondary ml-6"
        color="secondary"
        @click="isCreate() ? postBargeManagement() : putBargeManagement()"
      >
        {{ isCreate() ? 'Submit' : 'Save' }}
      </vs-button>
      <vs-button
        v-if="isDetail() && isUserCanAccessMenu(permission.name, 'edit')"
        class="button shadow-secondary ml-6"
        color="secondary"
        @click="$router.push({ name: 'SettingsBargeManagementEdit', params: { id: $route.params.id || bargeManagementId } })"
      >
        Edit
      </vs-button>
    </div>
    <pop-up
      :active.sync="popUp"
      @close="$router.push({ name: 'SettingsBargeManagementList' })"
      @ok="$router.push({ name: 'SettingsBargeManagementList' })"
    >
      You have successfully {{ isCreate() ? 'add' : 'save' }} barge
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import Autonumeric from '@/components/autonumeric'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import { PAGE_TITLE } from '@/fixtures/Constants'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    Autonumeric,
    PopUp,
    SelectWithSearch
  },
  data() {
    return {
      popUp: false,
      isActive: true,
      image: null,
      form: {
        name: {
          isValid: true,
          value: null
        },
        owner_id: {
          isValid: true,
          value: null
        },
        barge_size: {
          isValid: true,
          value: null
        },
        barge_capacity: {
          isValid: true,
          value: null
        }
      },
      ownerOptions: [],
      numericOptions: {
        allowDecimalPadding: false,
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalCharacterAlternative: '.',
        roundingMethod: 'U',
        minimumValue: '0'
      }
    }
  },
  computed: {
    ...mapGetters({
      ownerNames: 'BargeManagement/ownerNames',
      bargeManagementId: 'BargeManagement/bargeManagementId',
      bargeManagementDetail: 'BargeManagement/bargeManagementDetail'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    ownerNames: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.ownerOptions = val.map(item => ({ text: item.owner_name, value: item.owner_id }))
        }
      }
    },
    bargeManagementDetail: {
      handler(val) {
        if (val) {
          Object.keys(this.form).forEach(key => {
            this.form[key].value = val[key]
          })
          this.image = val.image_file ? Buffer.from(val.image_file, 'binary').toString() : null
          this.isActive = val.active
        }
      }
    }
  },
  async mounted() {
    await this.fetchOwnerNames()
    if (!this.isCreate()) {
      await this.fetchBargeManagementDetail()
    }
  },
  methods: {
    async fetchOwnerNames() {
      try {
        await this.$store.dispatch('BargeManagement/fetchOwnerNames')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner Name, please try again')
      }
    },
    async fetchBargeManagementDetail() {
      try {
        await this.$store.dispatch('BargeManagement/fetchBargeManagementDetail', this.$route.params.id || this.bargeManagementId)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch detail, please try again')
      }
    },
    async postBargeManagement() {
      try {
        if (this.validate()) {
          this.showFailedNotification(this, 'Some fields is empty, please fill it first')
          return
        }
        await this.$store.dispatch('BargeManagement/postBargeManagement', {
          ...Object.keys(this.form).reduce((item, key) => {
            item[key] = this.form[key].value
            return item
          }, {}),
          owner_name: this.ownerOptions.find(item => item.value === this.form.owner_id.value).text,
          image_file: this.image || ''
        })
        this.popUp = true
      } catch ({ error }) {
        this.showFailedNotification(this, error?.message || 'Failed to submit Barge Management, please try again')
      }
    },
    async putBargeManagement() {
      try {
        if (this.validate()) {
          this.showFailedNotification(this, 'Some fields is empty, please fill it first')
          return
        }
        await this.$store.dispatch('BargeManagement/putBargeManagement', {
          id: this.$route.params.id || this.bargeManagementId,
          form: {
            ...Object.keys(this.form).reduce((item, key) => {
              item[key] = this.form[key].value
              return item
            }, {}),
            owner_name: this.ownerOptions.find(item => item.value === this.form.owner_id.value).text,
            image_file: this.image || '',
            active: this.isActive
          }
        })
        this.popUp = true
      } catch ({ error }) {
        this.showFailedNotification(this, error?.message || 'Failed to save Barge Management, please try again')
      }
    },
    isCreate() {
      return this.$route.name.includes('Create')
    },
    isDetail() {
      return this.$route.name.includes('Detail')
    },
    imageToBase64(e) {
      const file = e.target.files || e.dataTransfer.files || this.$refs.inputImage.files
      if (file.length === 1) {
        if (file[0].size > 10485760) {
          this.showFailedNotification(this, 'Image may not be greater than 10MB')
          return
        }
        this.getBase64(file[0],({ target: { result } }) => {
          this.image = result
          this.$refs.inputImage.blur()
        })
      }
    },
    getBase64(file, callback) {
      const reader = new FileReader()
      reader.onload = callback
      reader.readAsDataURL(file)
    },
    clearImage() {
      this.image = null
      this.$refs.inputImage.blur()
    },
    validate() {
      Object.keys(this.form).forEach(key => {
        this.form[key].isValid = !!this.form[key].value
      })

      return Object.keys(this.form).some(key => !this.form[key].isValid)
    }
  }
}
</script>

<style lang="scss" scoped>
.button {
  @apply py-4 #{!important};
  @apply rounded-lg;
  @apply border-2;
  @apply font-semibold;
  @apply min-w-button;
  @apply text-base;
  height: calc( 1rem * 10 / 3 );

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

.image-wrapper {
  @apply flex;
  @apply flex-wrap;
  @apply relative;
  @apply items-center;
  @apply justify-center;
  @apply rounded-2xl;
  background-color: #F1F1FD;
  min-height: calc(128px + 6.5rem);

  .image {
    @apply rounded-2xl;
    @apply w-full;
  }
}

.bg-icon {
  @apply absolute;
  @apply cursor-pointer;
  @apply rounded-lg;
  @apply w-8;
  @apply h-8;
  top: 1rem;
  right: 1rem;
  background-color: rgba(252, 219, 208, 0.36);

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}
</style>
